import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { campaignsDataAtom, organizationDataAtom } from '@/store';
import {
  useAccountConversionActionsReportQuery, useAccountSearchTermsReportQuery,
  useActivityFeedQuery, useAdPerformanceReportQuery, useAllActionLogsQuery,
  campaignsIncludingExternalQueryOptions, useCampaignStatusReportQuery,
  usePerformanceInsightsQuery, usePerformanceTimeSeriesQuery,
  usePreviousPerformanceTimeSeriesQuery,
} from '@/lib/query-options';
import { getCampaignType } from '@/lib/types';
import { campaignQueryOptions } from '@/lib/query-options';
import { previousDateWindow, resolveDateWindow } from '@/lib/performance/date-window';
import type { CampaignData, ActionLog, Ad } from '@/lib/types';
import type { AdPerformanceData, AccountSearchTerm, ActivityEvent, CampaignStatusData } from '@/lib/apis';
import { CHART_PURPLE } from '@/components/performance/cells';
import type { TypeMetrics, CampaignMetrics } from '@/components/performance/tabs/campaigns-tab';
import { useQueries, useQuery } from '@tanstack/react-query';

const TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  prospecting: { label: 'Prospecting', color: 'bg-[#290BD9]/10 text-[#290BD9]' },
  brand: { label: 'Brand', color: 'bg-purple-100 text-purple-700' },
  competitor: { label: 'Competitor', color: 'bg-violet-100 text-violet-700' },
  icp: { label: 'ICP', color: 'bg-teal-100 text-teal-700' },
  hipo: { label: 'High Performance', color: 'bg-pink-100 text-pink-700' },
  location: { label: 'Location', color: 'bg-indigo-100 text-indigo-700' },
  external: { label: 'External', color: 'bg-gray-100 text-gray-700' },
  onboarding: { label: 'Prospecting', color: 'bg-[#290BD9]/10 text-[#290BD9]' },
  focused: { label: 'Prospecting', color: 'bg-[#290BD9]/10 text-[#290BD9]' },
  standard: { label: 'Prospecting', color: 'bg-[#290BD9]/10 text-[#290BD9]' },
  halo: { label: 'Prospecting', color: 'bg-[#290BD9]/10 text-[#290BD9]' },
};

// Classification used across the Performance views. Mirrors the campaigns
// sidebar (campaign-sidebar.tsx): the campaign's type comes from
// getCampaignType (campaign.goal); "external" is a separate dimension driven
// by campaign.origin, NOT by a failed ad->campaign lookup. See #4254.
export function classifyCampaign(c: CampaignData): string {
  return c.origin === 'external' ? 'external' : getCampaignType(c);
}

export interface UsePerformanceDataOptions {
  includeExternal: boolean;
  tab: 'overview' | 'campaigns' | 'ads' | 'keywords' | 'search-terms' | 'audience' | 'budget' | 'activity';
  campaignSubView: string;
  comparisonMode: string;
  selectedCampaignIds: Set<number>;
  chartDateRange: number;
  chartGroupBy: 'day' | 'week' | 'month';
  showUserEdits: boolean;
  selectedMetric: string;
  /** Toolbar day count; 0 = "All time" (resolved by the shared date-window helper, #6274). */
  selectedDays: number;
  /** #6500 — explicit picker range; when set it overrides selectedDays. */
  customRange?: { start: string; end: string } | null;
  typeFilter: (type: string) => boolean;
  cs: string;
}

/**
 * The one campaign-scoping rule for the consolidated Performance surface
 * (#6275/#6277): the org's campaigns (externals merged in when the toggle is
 * on), narrowed by the toolbar's type chips and campaign picker. Shared by
 * usePerformanceData, the Overview top block, and the All-scope merged panels
 * so switching the platform scope never changes the GOOGLE numbers.
 */
export function useScopedCampaigns({
  includeExternal,
  typeFilter,
  selectedCampaignIds,
}: {
  includeExternal: boolean;
  typeFilter: (type: string) => boolean;
  selectedCampaignIds: Set<number>;
}) {
  const glitchCampaigns = useAtomValue(campaignsDataAtom);
  const organizationData = useAtomValue(organizationDataAtom);
  const { data: allCampaigns, error: allCampaignsError } = useQuery({
    ...campaignsIncludingExternalQueryOptions(organizationData?.id),
    enabled: includeExternal && !!organizationData?.id,
  });
  // Use glitchCampaigns (from atom, has googleads_campaign) as base.
  // When includeExternal, merge in external-only campaigns from the query.
  const campaigns = useMemo(() => {
    if (!includeExternal || !allCampaigns) return glitchCampaigns;
    const glitchIds = new Set(glitchCampaigns.map(c => c.id));
    const externals = allCampaigns.filter(c => !glitchIds.has(c.id));
    return [...glitchCampaigns, ...externals];
  }, [includeExternal, allCampaigns, glitchCampaigns]);

  // External campaigns bypass the type chips (external is orthogonal to type;
  // they're only present at all when the include-external toggle merged them in).
  const scopedCampaigns = useMemo(() => {
    let filtered = campaigns.filter(c => {
      const type = classifyCampaign(c);
      return type === 'external' ? includeExternal : typeFilter(type);
    });
    if (selectedCampaignIds.size > 0) {
      filtered = filtered.filter(c => selectedCampaignIds.has(c.id));
    }
    return filtered;
  }, [campaigns, typeFilter, selectedCampaignIds, includeExternal]);

  // The googleads-id list report queries narrow by (Glitch-id joins stay
  // available via `campaigns` / `scopedCampaigns`).
  const scopedGoogleAdsIds = useMemo(() =>
    scopedCampaigns
      .map(c => c.googleads_campaign?.googleads_campaign_id)
      .filter(Boolean)
      .join(','),
    [scopedCampaigns]
  );

  return { campaigns, scopedCampaigns, scopedGoogleAdsIds, allCampaignsError };
}

/**
 * Local ads for never-live campaigns (ruling 09-09). The org campaigns list
 * is deliberately light (#4760 — CampaignReadWithoutAssociations, no nested
 * ads), so the draft-structure seeding on Creative & Ads fans out the full
 * campaign detail query for the never-live campaigns only. N is small (an
 * org's drafts), each response is cached per slug by React Query, and the
 * fetches are gated on the sub-views that render the seeds.
 */
export function useNeverLiveAds(campaigns: CampaignData[], enabled = true): Map<number, Ad[]> {
  const neverLive = useMemo(
    () => campaigns.filter((c) => !c.googleads_campaign?.googleads_campaign_id || c.state === 'draft'),
    [campaigns],
  );
  const queries = useQueries({
    queries: neverLive.map((c) => ({
      ...campaignQueryOptions(c.slug),
      enabled: enabled && !!c.slug,
    })),
  });
  return useMemo(() => {
    const byCampaignId = new Map<number, Ad[]>();
    queries.forEach((q, i) => {
      const ads = q.data?.ads;
      if (ads && ads.length > 0) byCampaignId.set(neverLive[i].id, ads);
    });
    return byCampaignId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neverLive, ...queries.map((q) => q.data)]);
}

export function usePerformanceData(options: UsePerformanceDataOptions) {
  const {
    includeExternal, tab, campaignSubView, comparisonMode, selectedCampaignIds,
    chartDateRange, chartGroupBy, showUserEdits, selectedMetric, selectedDays,
    customRange, typeFilter, cs,
  } = options;
  const organizationData = useAtomValue(organizationDataAtom);
  const { campaigns, scopedCampaigns, scopedGoogleAdsIds, allCampaignsError } = useScopedCampaigns({
    includeExternal,
    typeFilter,
    selectedCampaignIds,
  });

  // The one date window every report query derives from (#6274). Exact day
  // count from the toolbar — no more LAST_90_DAYS bucketing.
  const dateRangeFilter = useMemo(() => resolveDateWindow(selectedDays, customRange ?? null), [selectedDays, customRange]);

  // #4302 — human-readable date-range label for CSV export headers
  // ("11 May 2026 - 17 May 2026") matching the Google Ads UI export shape.
  const dateRangeLabel = useMemo(() => {
    const fmt = (iso: string): string => {
      const d = new Date(iso);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };
    return `${fmt(dateRangeFilter.start_date)} - ${fmt(dateRangeFilter.end_date)}`;
  }, [dateRangeFilter.start_date, dateRangeFilter.end_date]);

  // Compare-period date range (#4293 'previous', #4309 shift modes). Same
  // length as the current window in every mode — see previousDateWindow (#6274).
  // For 'none' the value is unused (the second fetch is disabled by comparisonMode).
  const previousDateRangeFilter = useMemo(
    () => previousDateWindow(dateRangeFilter, comparisonMode),
    [dateRangeFilter, comparisonMode],
  );

  // #6128 — every campaign the perf surface considers, in ANY state. The old
  // `state === LIVE || PAUSED` filter was an accidental client-side narrowing (the backend
  // returns campaigns regardless of state): it hid serving campaigns, and — because the join
  // map below is built from this — mislabeled them "external" when a report row failed to find
  // its record. State is a display concern, not something collected here.
  const activeCampaigns = campaigns;

  // Ad-performance rows arrive with campaign_id while a campaign carries
  // googleads_campaign.googleads_campaign_id; the runtime types differ
  // (number vs string), so a strict === silently missed and every internal
  // campaign fell through to "external". Normalise to string keys. See #4254.
  const campaignByGoogleId = useMemo(() => {
    const map = new Map<string, CampaignData>();
    for (const c of activeCampaigns) {
      const gid = c.googleads_campaign?.googleads_campaign_id;
      if (gid != null) map.set(String(gid), c);
    }
    return map;
  }, [activeCampaigns]);

  // #6275 — the id list every report query narrows by, applying the toolbar's
  // type chips and campaign picker SERVER-side (previously only the chart was
  // filtered and every report ran account-wide). Built in useScopedCampaigns.
  const campaignIds = scopedGoogleAdsIds;

  // Ad-level performance data. Explicit start/end (the backend prefers them
  // over date_range) so the window is the exact toolbar day count (#6274).
  // Disabled when the filter matches no campaigns — an unset campaign_ids
  // param means ACCOUNT-WIDE to the backend, which would silently replace
  // "you filtered everything out" with unfiltered data (#6275).
  const { data: adData, isLoading: isAdLoading } = useAdPerformanceReportQuery(
    campaignIds || undefined,
    undefined,
    {
      startDate: dateRangeFilter.start_date,
      endDate: dateRangeFilter.end_date,
      enabled: campaignIds.length > 0,
    },
  );

  // Previous-period ad performance, only fetched when comparison is on (#4293).
  // The backend accepts explicit start_date / end_date for this case; the
  // request is cached independently in React Query by the date range.
  // #4309 — fires under every non-'none' mode (previous / previous-week /
  // previous-month / previous-year); previousDateRangeFilter handles the
  // date math, this query is otherwise unchanged across all modes.
  // #6276 — gated off the Keywords / Search Terms tabs: they render no delta
  // columns, so the previous-period fetch there was pure discarded quota.
  const compareOn = comparisonMode !== 'none';
  const comparisonRendered = tab !== 'keywords' && tab !== 'search-terms';
  const { data: previousAdData } = useAdPerformanceReportQuery(
    campaignIds || undefined,
    undefined,
    {
      startDate: previousDateRangeFilter.start_date,
      endDate: previousDateRangeFilter.end_date,
      enabled: compareOn && comparisonRendered && !!campaignIds,
    },
  );

  // Per-campaign diagnostic fields for the Campaigns table (#4300):
  // status + reasons, Search IS, Search Lost IS (budget/rank), optimisation
  // score, bid strategy. Cached independently by date range.
  const { data: campaignStatusData } = useCampaignStatusReportQuery(
    campaignIds || '',
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    { enabled: !!campaignIds },
  );
  const campaignStatusByGoogleId = useMemo<Map<string, CampaignStatusData>>(() => {
    const map = new Map<string, CampaignStatusData>();
    for (const row of campaignStatusData ?? []) {
      map.set(row.campaign_id, row);
    }
    return map;
  }, [campaignStatusData]);

  // #4296 — account-wide search terms. Up to 2,000 rows per account per
  // period — also lazy-loaded so non-search-terms users don't burn quota.
  // #4307 — Overview's chip-cloud widget shares the same dataset; one fetch
  // serves both surfaces via React Query's cache.
  const { data: searchTermsData, isLoading: isSearchTermsLoading } = useAccountSearchTermsReportQuery(
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    campaignIds || undefined,
    1,
    { enabled: !!campaignIds && (tab === 'search-terms' || tab === 'overview') },
  );

  // #4297 — merged activity feed (Google Ads change_event + Glitch ActionLog).
  // change_event has a 30-day retention ceiling — older dates return nothing,
  // which the empty state explains. Also enabled on Overview so #4259's
  // Account Snapshot can compute change velocity from the same dataset
  // (React Query dedupes the single shared call).
  // Enabled on: the Activity tab itself, Overview (Account Snapshot reads
  // change velocity from this dataset), and any chart-bearing tab when the
  // user-edits overlay toggle is on. React Query dedupes per queryKey, so the
  // single shared fetch serves every consumer.
  const activityNeededForChart = showUserEdits && (tab === 'campaigns' || tab === 'ads');
  // #6487 — requires BOTH an org id and a non-empty scoped id list: with no
  // campaign_ids the endpoint returns the ACCOUNT-WIDE feed, silently swapping
  // "the filter matched nothing" for unscoped data (same trap as #6275).
  const { data: activityData, isLoading: isActivityLoading } = useActivityFeedQuery(
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    organizationData?.id,
    campaignIds || undefined,
    {
      enabled:
        (tab === 'activity' || tab === 'overview' || activityNeededForChart) &&
        !!organizationData?.id &&
        campaignIds.length > 0,
    },
  );

  // #4304 — LLM-generated narrative insights for the Overview tab. Lazy:
  // only fires when the user is on Overview AND has campaign IDs. Stale-
  // time is long (30 min) because LLM responses are expensive.
  const { data: insightsData, isLoading: isInsightsLoading } = usePerformanceInsightsQuery(
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    campaignIds || '',
    { enabled: tab === 'overview' && !!campaignIds },
  );

  // #4301 — per-campaign × conversion-action breakdown. Powers two surfaces:
  // the Campaigns 'By Conversion Action' grouping and the Overview Conversion-
  // Mix donut. Enabled when either is visible, to avoid double-fetching when
  // the user toggles between them.
  const { data: conversionActionsData, isLoading: isConversionActionsLoading } = useAccountConversionActionsReportQuery(
    dateRangeFilter.start_date,
    dateRangeFilter.end_date,
    campaignIds || undefined,
    {
      enabled: !!campaignIds && (
        tab === 'overview' ||
        (tab === 'campaigns' && campaignSubView === 'by-conversion-action')
      ),
    },
  );

  // #4307 — Notable search terms grouped by campaign for the Overview chip
  // cloud. Top N per campaign by clicks; status-coloured. Limited so the
  // widget stays scannable.
  const notableSearchTermsByCampaign = useMemo(() => {
    if (!searchTermsData || searchTermsData.length === 0) return [];
    const groups = new Map<string, { campaign: string; terms: AccountSearchTerm[] }>();
    for (const row of searchTermsData) {
      const key = row.campaign_name || row.campaign_id;
      const existing = groups.get(key) ?? { campaign: key, terms: [] };
      existing.terms.push(row);
      groups.set(key, existing);
    }
    // Within each group sort by clicks desc and keep top 10 to stay scannable.
    // Across groups, sort by group total clicks desc so the busiest campaign
    // shows first.
    return Array.from(groups.values())
      .map(g => ({
        campaign: g.campaign,
        terms: g.terms.sort((a, b) => b.clicks - a.clicks).slice(0, 10),
        totalClicks: g.terms.reduce((s, t) => s + t.clicks, 0),
      }))
      .sort((a, b) => b.totalClicks - a.totalClicks);
  }, [searchTermsData]);

  // Roll up per-action totals across all campaigns for the Overview donut.
  const conversionMixByAction = useMemo(() => {
    if (!conversionActionsData || conversionActionsData.length === 0) return [];
    const byName = new Map<string, { name: string; conversions: number; value: number }>();
    for (const row of conversionActionsData) {
      const name = row.conversion_action_name || 'Unknown';
      const existing = byName.get(name) ?? { name, conversions: 0, value: 0 };
      existing.conversions += row.conversions;
      existing.value += row.all_conversions_value;
      byName.set(name, existing);
    }
    return Array.from(byName.values()).sort((a, b) => b.conversions - a.conversions);
  }, [conversionActionsData]);

  // Time-series performance data — same scoped campaign set as the reports
  // (#6275), in Glitch-id space (the time-series route maps internal ids to
  // Google Ads ids server-side).
  const chartCampaignIds = useMemo(
    () => scopedCampaigns.map(c => c.id).join(','),
    [scopedCampaigns],
  );

  const chartTimeRange = chartDateRange <= 90 ? 'daily' : 'weekly';
  const chartDateRangeParam = `${chartDateRange || 365}d`;

  // #4319 — two separate fetches: one for the current period, one for the
  // previous (only when comparison is enabled). Replaces the old chartDateRange*2
  // trick which only worked for 'previous' mode because the URL-path date range
  // couldn't express week / month / year-ago slices. Each fetch is cached
  // independently by React Query under its own queryKey.
  // #6276 — the time-series (and its previous-period twin) only feed chartData
  // consumers: the Overview's spend-trend cards and alerts. The other tabs
  // never render it, yet every filter click there fired 1-2 GAQL-backed
  // requests whose output was discarded.
  const chartNeeded = tab === 'overview';
  const {
    data: timeSeriesRaw,
    isLoading: isTimeSeriesLoading,
    error: timeSeriesError,
  } = usePerformanceTimeSeriesQuery(
    chartTimeRange,
    chartDateRangeParam,
    chartCampaignIds,
    { startDate: dateRangeFilter.start_date, endDate: dateRangeFilter.end_date },
    chartNeeded,
  );

  const { data: previousTimeSeriesRaw, error: previousTimeSeriesError } = usePreviousPerformanceTimeSeriesQuery(
    chartTimeRange,
    chartDateRangeParam,
    chartCampaignIds,
    previousDateRangeFilter,
    chartNeeded && comparisonMode !== 'none' && chartDateRange > 0,
  );

  // The two responses are already date-aligned (each was fetched for its own
  // range), so no midpoint-split / *2-trick gymnastics. Downstream chartData
  // useMemo handles the end-aligned offset merge to cope with different bucket
  // counts when grouping by week or month.
  const timeSeriesData = timeSeriesRaw ?? [];
  const comparisonTimeSeriesData = comparisonMode !== 'none' ? (previousTimeSeriesRaw ?? []) : [];

  // Action logs for the ai-actions widget. organization_id is required (#6281):
  // without it the endpoint aggregates action logs across EVERY org the user
  // belongs to, so the widget showed other orgs' actions.
  const { data: actionLogsData } = useAllActionLogsQuery(
    {
      start_date: dateRangeFilter.start_date,
      end_date: dateRangeFilter.end_date,
      organization_id: organizationData?.id,
    },
    { skip: 0, limit: 100 },
  );

  const isLoading = isAdLoading || isTimeSeriesLoading;

  // Aggregate ad data into campaign metrics
  const campaignMetrics = useMemo<CampaignMetrics[]>(() => {
    if (!adData || adData.length === 0) return [];

    const byCampaign = new Map<string, { ads: AdPerformanceData[]; campaign?: CampaignData }>();
    for (const ad of adData) {
      const existing = byCampaign.get(ad.campaign_id) || { ads: [] };
      existing.ads.push(ad);
      if (!existing.campaign) {
        existing.campaign = campaignByGoogleId.get(String(ad.campaign_id));
      }
      byCampaign.set(ad.campaign_id, existing);
    }

    return Array.from(byCampaign.entries()).map(([, { ads, campaign }]) => {
      const impressions = ads.reduce((s, a) => s + a.impressions, 0);
      const clicks = ads.reduce((s, a) => s + a.clicks, 0);
      const conversions = Math.round(ads.reduce((s, a) => s + a.conversions, 0));
      const cost = Math.round(ads.reduce((s, a) => s + a.cost, 0) * 100) / 100;
      const conversionValue = Math.round(ads.reduce((s, a) => s + (a.all_conversions_value || 0), 0) * 100) / 100;
      // Carried per campaign so totals can respect the same filters as every
      // other number in the row (#6275) — previously summed from raw adData.
      const allConversions = ads.reduce((s, a) => s + (a.all_conversions || 0), 0);
      const viewThroughConversions = ads.reduce((s, a) => s + (a.view_through_conversions || 0), 0);
      // External iff the report row has no Glitch record, or the joined record is tagged
      // external (authoritative `origin`). No list-diff inference (#6128).
      const type = !campaign || campaign.origin === 'external'
        ? 'external'
        : classifyCampaign(campaign);
      const isExternal = type === 'external';
      const config = TYPE_CONFIG[type] || { label: 'External', color: 'bg-gray-100 text-gray-700' };

      return {
        id: campaign?.id || 0,
        // Keep the Google Ads campaign id alongside the Glitch id so #4300's
        // Campaigns-table cells can join into campaignStatusByGoogleId.
        googleadsCampaignId: campaign?.googleads_campaign?.googleads_campaign_id ?? null,
        name: campaign ? campaign.name : ads[0]?.campaign_name || 'Unknown',
        // Glitch lifecycle state (Live/Draft/Paused/…) + external flag, shown as columns on the
        // Campaigns table. Both come straight off the joined campaign — no extra query.
        state: campaign?.state ?? null,
        isExternal,
        type, typeLabel: config.label, typeColor: config.color,
        impressions, clicks, conversions, cost,
        ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
        cpc: clicks > 0 ? cost / clicks : 0,
        cpa: conversions > 0 ? cost / conversions : 0,
        roas: cost > 0 ? conversionValue / cost : 0,
        conversionValue, adCount: ads.length,
        allConversions, viewThroughConversions,
        budget: campaign?.budget || 0,
      };
    });
  }, [adData, campaignByGoogleId]);

  // Previous-period campaign totals keyed by Glitch campaign id (#4293).
  // Mirrors the same group-and-sum shape as campaignMetrics so the cell
  // renderer (next commit on this ticket) can join current and previous by id.
  // Empty when compare mode is off — cells then render without deltas.
  type PreviousCampaignTotals = {
    impressions: number;
    clicks: number;
    conversions: number;
    cost: number;
    conversionValue: number;
    ctr: number;
    cpc: number;
    cpa: number;
    roas: number;
  };
  const previousCampaignTotalsById = useMemo<Map<number, PreviousCampaignTotals>>(() => {
    const out = new Map<number, PreviousCampaignTotals>();
    if (comparisonMode === 'none' || !previousAdData || previousAdData.length === 0) return out;
    const byCampaign = new Map<string, AdPerformanceData[]>();
    for (const ad of previousAdData) {
      const list = byCampaign.get(ad.campaign_id) ?? [];
      list.push(ad);
      byCampaign.set(ad.campaign_id, list);
    }
    for (const [googleCampaignId, ads] of byCampaign) {
      const campaign = campaignByGoogleId.get(String(googleCampaignId));
      if (!campaign) continue;
      const impressions = ads.reduce((s, a) => s + a.impressions, 0);
      const clicks = ads.reduce((s, a) => s + a.clicks, 0);
      const conversions = Math.round(ads.reduce((s, a) => s + a.conversions, 0));
      const cost = Math.round(ads.reduce((s, a) => s + a.cost, 0) * 100) / 100;
      const conversionValue = Math.round(ads.reduce((s, a) => s + (a.all_conversions_value || 0), 0) * 100) / 100;
      out.set(campaign.id, {
        impressions, clicks, conversions, cost, conversionValue,
        ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
        cpc: clicks > 0 ? cost / clicks : 0,
        cpa: conversions > 0 ? cost / conversions : 0,
        roas: cost > 0 ? conversionValue / cost : 0,
      });
    }
    return out;
  }, [previousAdData, campaignByGoogleId, comparisonMode]);

  // Previous-period per-ad metrics keyed by Google Ads ad_id (#4293).
  // Drives Δ% in the Ads tab. Same shape as adMetrics minus the derived
  // display fields — only the numeric values the cells need.
  type PreviousAdTotals = {
    impressions: number;
    clicks: number;
    ctr: number;
    conversions: number;
    cost: number;
    cpa: number;
  };
  const previousAdMetricsByAdId = useMemo<Map<string, PreviousAdTotals>>(() => {
    const out = new Map<string, PreviousAdTotals>();
    if (comparisonMode === 'none' || !previousAdData || previousAdData.length === 0) return out;
    for (const ad of previousAdData) {
      const impressions = ad.impressions;
      const clicks = ad.clicks;
      const conversions = Math.round(ad.conversions);
      const cost = Math.round(ad.cost * 100) / 100;
      out.set(ad.ad_id, {
        impressions, clicks, conversions, cost,
        ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
        cpa: conversions > 0 ? Math.round((cost / conversions) * 100) / 100 : 0,
      });
    }
    return out;
  }, [previousAdData, comparisonMode]);

  // #4294 / #4299 — Ad-group rollup. Aggregates the same adData that powers
  // campaignMetrics but grouped by ad_group_id. Lets the Campaigns tab's
  // "By Ad Group" grouping show one row per ad group with summed metrics.
  const adGroupMetrics = useMemo(() => {
    if (!adData || adData.length === 0) return [];
    const byAdGroup = new Map<string, { ads: AdPerformanceData[]; first: AdPerformanceData }>();
    for (const ad of adData) {
      const existing = byAdGroup.get(ad.ad_group_id);
      if (existing) {
        existing.ads.push(ad);
      } else {
        byAdGroup.set(ad.ad_group_id, { ads: [ad], first: ad });
      }
    }
    return Array.from(byAdGroup.entries()).map(([adGroupId, { ads, first }]) => {
      const impressions = ads.reduce((s, a) => s + a.impressions, 0);
      const clicks = ads.reduce((s, a) => s + a.clicks, 0);
      const conversions = Math.round(ads.reduce((s, a) => s + a.conversions, 0));
      const cost = Math.round(ads.reduce((s, a) => s + a.cost, 0) * 100) / 100;
      const conversionValue = Math.round(ads.reduce((s, a) => s + (a.all_conversions_value || 0), 0) * 100) / 100;
      const campaign = campaignByGoogleId.get(String(first.campaign_id));
      const type = !campaign || campaign.origin === 'external' ? 'external' : classifyCampaign(campaign);
      const config = TYPE_CONFIG[type] || { label: 'External', color: 'bg-gray-100 text-gray-700' };
      return {
        adGroupId,
        adGroupName: first.ad_group_name || `Ad group ${adGroupId}`,
        campaignName: campaign ? campaign.name : first.campaign_name,
        type,
        typeLabel: config.label,
        typeColor: config.color,
        impressions, clicks, conversions, cost,
        ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
        cpa: conversions > 0 ? cost / conversions : 0,
        roas: cost > 0 ? conversionValue / cost : 0,
        adCount: ads.length,
      };
    }).filter(ag => typeFilter(ag.type))
      .sort((a, b) => b.impressions - a.impressions);
  }, [adData, campaignByGoogleId, typeFilter]);

  const filteredCampaigns = useMemo(() => {
    // Mirror the Ads table: external (non-Glitch) campaigns show only when the include-external
    // toggle is on; Glitch campaigns filter by their type chip (#6128).
    let filtered = campaignMetrics.filter(c => (c.isExternal ? includeExternal : typeFilter(c.type)));
    if (selectedCampaignIds.size > 0) {
      filtered = filtered.filter(c => selectedCampaignIds.has(c.id));
    }
    return filtered;
  }, [campaignMetrics, typeFilter, selectedCampaignIds, includeExternal]);

  // Aggregate by type — from the FILTERED campaign set (#6275) so the by-type
  // roll-up (type-efficiency widget, cost/conv-by-type, the Campaigns tab's
  // default sub-view) responds to the same filters as its siblings.
  const typeMetrics = useMemo<TypeMetrics[]>(() => {
    const byType = new Map<string, CampaignMetrics[]>();
    for (const cm of filteredCampaigns) {
      const existing = byType.get(cm.type) || [];
      existing.push(cm);
      byType.set(cm.type, existing);
    }
    return Array.from(byType.entries()).map(([type, cms]) => {
      const impressions = cms.reduce((s, c) => s + c.impressions, 0);
      const clicks = cms.reduce((s, c) => s + c.clicks, 0);
      const conversions = Math.round(cms.reduce((s, c) => s + c.conversions, 0));
      const cost = Math.round(cms.reduce((s, c) => s + c.cost, 0) * 100) / 100;
      const conversionValue = Math.round(cms.reduce((s, c) => s + c.conversionValue, 0) * 100) / 100;
      const config = TYPE_CONFIG[type] || { label: 'Other', color: 'bg-gray-100 text-gray-800' };
      return {
        type, label: config.label, color: config.color, campaigns: cms.length,
        impressions, clicks, conversions, cost,
        ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
        cpc: clicks > 0 ? cost / clicks : 0,
        cpa: conversions > 0 ? cost / conversions : 0,
        roas: cost > 0 ? conversionValue / cost : 0,
      };
    }).sort((a, b) => b.cost - a.cost);
  }, [filteredCampaigns]);

  // Totals — every number from the same filtered set. allConversions and
  // viewThrough previously summed the raw unfiltered adData, mixing
  // account-wide values into an otherwise filtered widget (#6275).
  const totals = useMemo(() => {
    const data = filteredCampaigns;
    const impressions = data.reduce((s, c) => s + c.impressions, 0);
    const clicks = data.reduce((s, c) => s + c.clicks, 0);
    const conversions = Math.round(data.reduce((s, c) => s + c.conversions, 0));
    const cost = Math.round(data.reduce((s, c) => s + c.cost, 0) * 100) / 100;
    const conversionValue = Math.round(data.reduce((s, c) => s + c.conversionValue, 0) * 100) / 100;
    const allConversions = Math.round(data.reduce((s, c) => s + c.allConversions, 0));
    const viewThrough = data.reduce((s, c) => s + c.viewThroughConversions, 0);
    return {
      impressions, clicks, conversions, cost, conversionValue,
      allConversions, viewThrough,
      ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
      cpc: clicks > 0 ? cost / clicks : 0,
      cpa: conversions > 0 ? cost / conversions : 0,
      roas: cost > 0 ? conversionValue / cost : 0,
      conversionRate: clicks > 0 ? (conversions / clicks) * 100 : 0,
    };
  }, [filteredCampaigns]);

  // Compare-period totals over the SAME filtered campaign set as `totals`,
  // so the Overview metric cards can show Δ% (#6276) — before this, comparison
  // mode was invisible on the consolidated Overview. Null when compare is off
  // or the previous period has no joined rows.
  const previousTotals = useMemo(() => {
    if (comparisonMode === 'none' || previousCampaignTotalsById.size === 0) return null;
    const rows = filteredCampaigns
      .map(c => previousCampaignTotalsById.get(c.id))
      .filter((r): r is NonNullable<typeof r> => !!r);
    if (rows.length === 0) return null;
    const impressions = rows.reduce((s, r) => s + r.impressions, 0);
    const clicks = rows.reduce((s, r) => s + r.clicks, 0);
    const conversions = Math.round(rows.reduce((s, r) => s + r.conversions, 0));
    const cost = Math.round(rows.reduce((s, r) => s + r.cost, 0) * 100) / 100;
    const conversionValue = Math.round(rows.reduce((s, r) => s + r.conversionValue, 0) * 100) / 100;
    return {
      impressions, clicks, conversions, cost, conversionValue,
      ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
      cpc: clicks > 0 ? cost / clicks : 0,
      cpa: conversions > 0 ? cost / conversions : 0,
      roas: cost > 0 ? conversionValue / cost : 0,
      conversionRate: clicks > 0 ? (conversions / clicks) * 100 : 0,
    };
  }, [comparisonMode, previousCampaignTotalsById, filteredCampaigns]);

  // Ad-level metrics
  const adMetrics = useMemo(() => {
    if (!adData || adData.length === 0) return [];
    return adData.map(ad => {
      const campaign = campaignByGoogleId.get(String(ad.campaign_id));
      const type = !campaign || campaign.origin === 'external'
        ? 'external'
        : classifyCampaign(campaign);
      const isExternal = type === 'external';
      const config = TYPE_CONFIG[type] || { label: 'External', color: 'bg-gray-100 text-gray-700' };
      // Match to Glitch ad by ad_group_id for linking
      const glitchAd = campaign?.ads?.find(
        a => a.googleads_ad_group?.googleads_ad_group_id === ad.ad_group_id
      );
      const adIndex = campaign?.ads?.indexOf(glitchAd!) ?? -1;
      const adDisplayName = ad.ad_name
        || (glitchAd && adIndex >= 0 ? `Ad ${adIndex + 1}` : null)
        || (ad.headlines?.length > 0 ? ad.headlines[0] : null)
        || ad.ad_group_name
        || 'Unnamed Ad';

      return {
        ...ad,
        campaignDisplayName: campaign ? campaign.name : ad.campaign_name,
        adDisplayName,
        campaignSlug: campaign?.slug,
        glitchAdId: glitchAd?.id,
        // Ads inherit their campaign's lifecycle state + external flag (Google Ads has no ad-level
        // state), so the Ads table can show the same State column as Campaigns.
        campaignState: campaign?.state ?? null,
        isExternal,
        typeLabel: config.label,
        typeColor: config.color,
        conversions: Math.round(ad.conversions),
        cost: Math.round(ad.cost * 100) / 100,
        cpa: ad.conversions > 0 ? Math.round(ad.cost / ad.conversions * 100) / 100 : 0,
        roas: ad.cost > 0 ? Math.round((ad.all_conversions_value || 0) / ad.cost * 10) / 10 : 0,
      };
    }).filter(ad => {
      const c = campaignByGoogleId.get(String(ad.campaign_id));
      // Match the row's own labeling: an ad with no Glitch record (or a record tagged
      // external) is external — show it only when the include-external toggle is on (#6128).
      if (!c || c.origin === 'external') return includeExternal;
      return typeFilter(classifyCampaign(c));
    });
  }, [adData, campaignByGoogleId, typeFilter, includeExternal]);

  // Time-series chart data with derived metrics and grouping
  const chartData = useMemo(() => {
    // A fetched-and-empty window synthesizes one all-zero point per day so
    // the chart keeps its populated shape (axes, legend, a flat line at
    // zero) instead of swapping to a different-shaped empty card. Only a
    // query that actually ran and returned no rows qualifies: disabled
    // (no campaign ids yet), loading, and errored series keep [] so their
    // dedicated states render, never a zero flash before real data arrives.
    const settledEmpty =
      timeSeriesRaw !== undefined && timeSeriesRaw.length === 0 && !isTimeSeriesLoading && !timeSeriesError;
    if ((!timeSeriesData || timeSeriesData.length === 0) && !settledEmpty) return [];
    const zeroPoint = (rawDate: string) => ({
      rawDate, spend: 0, impressions: 0, clicks: 0, conversions: 0,
      cpc: 0, cpa: 0, roas: 0, conversionValue: 0, cost: 0,
    });
    const zeroWindow = () => {
      const startMs = new Date(`${dateRangeFilter.start_date}T00:00:00Z`).getTime();
      const endMs = new Date(`${dateRangeFilter.end_date}T00:00:00Z`).getTime();
      const days: ReturnType<typeof zeroPoint>[] = [];
      // NaN bounds fall through to zero iterations; 400 caps all-time windows.
      for (let t = startMs; t <= endMs && days.length < 400; t += 86_400_000) {
        days.push(zeroPoint(new Date(t).toISOString().slice(0, 10)));
      }
      return days;
    };

    // Build daily points with all metrics
    const daily = (settledEmpty ? zeroWindow() : timeSeriesData.map(d => {
      const cost = d.cost || 0;
      const clicks = d.clicks || 0;
      const conversions = d.conversions || 0;
      const conversionValue = d.all_conversions_value || 0;
      return {
        rawDate: d.date,
        spend: cost,
        impressions: d.impressions || 0,
        clicks,
        conversions,
        cpc: clicks > 0 ? cost / clicks : 0,
        cpa: conversions > 0 ? cost / conversions : 0,
        roas: cost > 0 ? conversionValue / cost : 0,
        conversionValue,
        cost,
      };
    })).sort((a, b) => a.rawDate.localeCompare(b.rawDate));

    // Group if needed
    const grouped = chartGroupBy === 'day' ? daily : (() => {
      const groups = new Map<string, typeof daily>();
      for (const pt of daily) {
        const d = new Date(pt.rawDate);
        const key = chartGroupBy === 'week'
          ? (() => { const w = new Date(d); w.setDate(w.getDate() - ((w.getDay() + 6) % 7)); return w.toISOString().split('T')[0]; })()
          : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(pt);
      }
      return Array.from(groups.entries()).map(([key, pts]) => {
        const totalSpend = pts.reduce((s, p) => s + p.spend, 0);
        const totalClicks = pts.reduce((s, p) => s + p.clicks, 0);
        const totalConversions = pts.reduce((s, p) => s + p.conversions, 0);
        const totalConversionValue = pts.reduce((s, p) => s + p.conversionValue, 0);
        return {
          rawDate: key,
          spend: totalSpend, impressions: pts.reduce((s, p) => s + p.impressions, 0),
          clicks: totalClicks, conversions: totalConversions,
          cpc: totalClicks > 0 ? totalSpend / totalClicks : 0,
          cpa: totalConversions > 0 ? totalSpend / totalConversions : 0,
          roas: totalSpend > 0 ? totalConversionValue / totalSpend : 0,
          conversionValue: totalConversionValue, cost: totalSpend,
        };
      });
    })();

    // Build comparison data (same process for previous period)
    const buildMetrics = (raw: typeof timeSeriesData) => {
      const pts = raw.map(d => {
        const c = d.cost || 0; const cl = d.clicks || 0; const cv = d.conversions || 0; const cvv = d.all_conversions_value || 0;
        return { rawDate: d.date, spend: c, impressions: d.impressions || 0, clicks: cl, conversions: cv, cpc: cl > 0 ? c / cl : 0, cpa: cv > 0 ? c / cv : 0, roas: c > 0 ? cvv / c : 0, conversionValue: cvv, cost: c };
      }).sort((a, b) => a.rawDate.localeCompare(b.rawDate));
      if (chartGroupBy === 'day') return pts;
      const grps = new Map<string, typeof pts>();
      for (const pt of pts) {
        const dt = new Date(pt.rawDate);
        const key = chartGroupBy === 'week'
          ? (() => { const w = new Date(dt); w.setDate(w.getDate() - ((w.getDay() + 6) % 7)); return w.toISOString().split('T')[0]; })()
          : `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-01`;
        if (!grps.has(key)) grps.set(key, []);
        grps.get(key)!.push(pt);
      }
      return Array.from(grps.entries()).map(([key, gpts]) => {
        const ts = gpts.reduce((s, p) => s + p.spend, 0); const tc = gpts.reduce((s, p) => s + p.clicks, 0);
        const tcv = gpts.reduce((s, p) => s + p.conversions, 0); const tcvv = gpts.reduce((s, p) => s + p.conversionValue, 0);
        return { rawDate: key, spend: ts, impressions: gpts.reduce((s, p) => s + p.impressions, 0), clicks: tc, conversions: tcv, cpc: tc > 0 ? ts / tc : 0, cpa: tcv > 0 ? ts / tcv : 0, roas: ts > 0 ? tcvv / ts : 0, conversionValue: tcvv, cost: ts };
      });
    };

    const prevGrouped = comparisonTimeSeriesData.length > 0 ? buildMetrics(comparisonTimeSeriesData) : [];

    // Align comparison data by offset from end of period (handles different group counts)
    const prevOffset = prevGrouped.length - grouped.length;

    // Format dates and merge comparison by offset
    return grouped.map((d, i) => {
      const prevIdx = i + prevOffset;
      const prev = prevIdx >= 0 && prevIdx < prevGrouped.length ? prevGrouped[prevIdx] : null;
      // Weekend background flag — 1 for Sat/Sun, undefined otherwise.
      // Consumed by a hidden-axis <Bar> layer behind the lines so weekend
      // days get a full-column tinted background (categorical ReferenceArea
      // can't span a whole bin without weekday spill).
      const dow = new Date(d.rawDate).getDay(); // 0 = Sun, 6 = Sat
      const _weekendBg = (chartGroupBy === 'day' && (dow === 0 || dow === 6)) ? 1 : undefined;
      return {
        ...d,
        date: chartGroupBy === 'month'
          ? new Date(d.rawDate).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
          : new Date(d.rawDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        _weekendBg,
        ...(prev ? {
          prev_spend: prev.spend, prev_impressions: prev.impressions,
          prev_clicks: prev.clicks, prev_conversions: prev.conversions,
          prev_cpc: prev.cpc, prev_cpa: prev.cpa,
          prev_roas: prev.roas, prev_conversionValue: prev.conversionValue,
        } : {}),
      };
    });
  }, [timeSeriesRaw, timeSeriesData, comparisonTimeSeriesData, chartGroupBy, isTimeSeriesLoading, timeSeriesError, dateRangeFilter]);

  // Delta summary for comparison cards
  const deltaCards = useMemo(() => {
    if (chartData.length === 0) return [];
    // Each card carries its label, aggregation kind (sum / derived), and
    // formatting hint. Sums get a "Total" prefix; derived ratios stay bare
    // since they're period values by definition.
    // Sum tiles follow the surface's metric order (ruling 09-08): cost,
    // impressions, clicks, conversions — then the derived ratios.
    const metricsMeta: Array<{ key: string; label: string; kind: 'sum' | 'derived' }> = [
      { key: 'spend',       label: 'Total Cost',        kind: 'sum' },
      { key: 'impressions', label: 'Total Impressions', kind: 'sum' },
      { key: 'clicks',      label: 'Total Clicks',      kind: 'sum' },
      { key: 'conversions', label: 'Total Conversions', kind: 'sum' },
      { key: 'cpa',         label: 'CPA',               kind: 'derived' },
      { key: 'cpc',         label: 'CPC',               kind: 'derived' },
      { key: 'roas',        label: 'ROAS',              kind: 'derived' },
    ];
    const lowerIsBetter = new Set(['cpa', 'cpc']);
    const isCurrency = new Set(['spend', 'cpa', 'cpc']);

    // Pre-compute component sums so the derived metrics use proper period
    // math (sum(cost) / sum(conversions), etc.) instead of summing per-row
    // ratios. Tracks both current and previous-period sums in one pass.
    const sums = chartData.reduce((acc, d) => {
      const row = d as unknown as Record<string, number>;
      acc.cur.spend += row.spend || 0;
      acc.cur.clicks += row.clicks || 0;
      acc.cur.conversions += row.conversions || 0;
      acc.cur.impressions += row.impressions || 0;
      acc.cur.conversionValue += row.conversionValue || 0;
      acc.prev.spend += row.prev_spend || 0;
      acc.prev.clicks += row.prev_clicks || 0;
      acc.prev.conversions += row.prev_conversions || 0;
      acc.prev.impressions += row.prev_impressions || 0;
      acc.prev.conversionValue += row.prev_conversionValue || 0;
      return acc;
    }, {
      cur:  { spend: 0, clicks: 0, conversions: 0, impressions: 0, conversionValue: 0 },
      prev: { spend: 0, clicks: 0, conversions: 0, impressions: 0, conversionValue: 0 },
    });

    const valueFor = (key: string, s: typeof sums.cur): number => {
      switch (key) {
        case 'clicks':       return s.clicks;
        case 'conversions':  return s.conversions;
        case 'impressions':  return s.impressions;
        case 'spend':        return s.spend;
        case 'cpa':          return s.conversions > 0 ? s.spend / s.conversions : 0;
        case 'cpc':          return s.clicks > 0 ? s.spend / s.clicks : 0;
        case 'roas':         return s.spend > 0 ? s.conversionValue / s.spend : 0;
        default:             return 0;
      }
    };

    // A derived ratio with a zero denominator is undefined, not 0 — the tile
    // dashes with a note instead of implying free conversions or no return.
    const unmeasuredNoteFor = (key: string, s: typeof sums.cur): string | null => {
      switch (key) {
        case 'cpa':  return s.conversions === 0 ? 'No conversions in this period.' : null;
        case 'cpc':  return s.clicks === 0 ? 'No clicks in this period.' : null;
        case 'roas': return s.conversionValue === 0
          ? 'No conversion values recorded in this period.'
          : s.spend === 0 ? 'No cost in this period.' : null;
        default:     return null;
      }
    };

    return metricsMeta.map(({ key, label, kind }) => {
      const current = valueFor(key, sums.cur);
      const previous = valueFor(key, sums.prev);
      const unmeasuredNote = kind === 'derived' ? unmeasuredNoteFor(key, sums.cur) : null;
      const hasComparison = comparisonMode !== 'none' && previous > 0 && !unmeasuredNote;
      const delta = hasComparison ? ((current - previous) / previous) * 100 : 0;
      const improving = lowerIsBetter.has(key) ? delta < 0 : delta > 0;
      return {
        key, label, kind, current, previous, delta, hasComparison, improving,
        isCurrency: isCurrency.has(key), lowerIsBetter: lowerIsBetter.has(key),
        unmeasured: !!unmeasuredNote, unmeasuredNote: unmeasuredNote ?? undefined,
      };
      // ROAS without tracked conversion values is axed rather than dashed
      // (ruling 09-08): a permanently empty tile is noise for lead-gen
      // accounts. It reappears on its own where values exist.
    }).filter((card) => !(card.key === 'roas' && card.unmeasured));
  }, [chartData, comparisonMode]);

  // Action logs processed for timeline and chart overlay
  const actionLogs = useMemo<ActionLog[]>(() => {
    if (!actionLogsData?.items) return [];
    return actionLogsData.items
      .filter(a => !a.is_undone)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [actionLogsData]);

  // Map action logs to chart dates for overlay dots
  const actionDatesOnChart = useMemo(() => {
    if (chartData.length === 0 || actionLogs.length === 0) return [];
    const dateSet = new Map<string, ActionLog[]>();
    for (const action of actionLogs) {
      const actionDate = new Date(action.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      const existing = dateSet.get(actionDate) || [];
      existing.push(action);
      dateSet.set(actionDate, existing);
    }
    return chartData
      .filter(d => dateSet.has(d.date))
      .map(d => ({ date: d.date, value: (d as unknown as Record<string, number>)[selectedMetric] || 0, count: dateSet.get(d.date)!.length, actions: dateSet.get(d.date)! }));
  }, [chartData, actionLogs, selectedMetric]);

  // User-edit dots — Google Ads change_event rows where source === 'user'.
  // Same date-bucketing logic as actionDatesOnChart so the two series share
  // the X-axis but are independently togglable + visually distinct.
  const userEditDatesOnChart = useMemo(() => {
    if (chartData.length === 0 || !Array.isArray(activityData) || activityData.length === 0) return [];
    const userEvents = activityData.filter(e => e.source === 'user' && e.timestamp);
    if (userEvents.length === 0) return [];
    const dateSet = new Map<string, ActivityEvent[]>();
    for (const event of userEvents) {
      const d = new Date(event.timestamp!).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      const existing = dateSet.get(d) || [];
      existing.push(event);
      dateSet.set(d, existing);
    }
    return chartData
      .filter(d => dateSet.has(d.date))
      .map(d => ({ date: d.date, value: (d as unknown as Record<string, number>)[selectedMetric] || 0, count: dateSet.get(d.date)!.length, events: dateSet.get(d.date)! }));
  }, [chartData, activityData, selectedMetric]);

  // Has any weekend day at all on screen? — drives whether we bother
  // rendering the hidden Bar / Y-axis pair that shades weekend columns.
  const hasWeekendDays = useMemo<boolean>(
    () => chartGroupBy === 'day' && chartData.some(d => (d as { _weekendBg?: number })._weekendBg === 1),
    [chartData, chartGroupBy],
  );

  // Budget utilisation data
  const budgetUtilisation = useMemo(() => {
    const days = dateRangeFilter.days;
    return campaignMetrics
      .map(c => {
        // #3960 — prefer the live Google Ads daily budget from #4300's
        // campaign-status report; fall back to the Glitch DB value. This
        // keeps external/out-of-sync campaigns visible on the Budget tab
        // instead of being silently filtered out.
        const liveBudget = c.googleadsCampaignId
          ? campaignStatusByGoogleId.get(c.googleadsCampaignId)?.daily_budget ?? null
          : null;
        const dailyBudget = liveBudget && liveBudget > 0 ? liveBudget : c.budget;
        const dailySpend = c.cost / days;
        const utilisation = dailyBudget > 0 ? (dailySpend / dailyBudget) * 100 : 0;
        const config = TYPE_CONFIG[c.type] || { label: 'Other', color: 'bg-gray-100 text-gray-800' };
        return {
          name: c.name,
          type: c.type,
          typeLabel: config.label,
          typeColor: config.color,
          googleadsCampaignId: c.googleadsCampaignId,
          dailyBudget,
          dailySpend,
          utilisation: Math.min(utilisation, 100),
          color: CHART_PURPLE[Object.keys(TYPE_CONFIG).indexOf(c.type) % CHART_PURPLE.length] || CHART_PURPLE[0],
        };
      })
      .filter(row => row.dailyBudget > 0)
      .sort((a, b) => b.utilisation - a.utilisation);
  }, [campaignMetrics, dateRangeFilter.days, campaignStatusByGoogleId]);

  // #4259 — Account Snapshot tiles. Four account-level numbers the team
  // currently assembles by hand from separate Google Ads exports:
  // optimisation score, Search IS coverage, budget utilisation, change
  // velocity. Each is an aggregate over loaded data — no extra queries.
  const accountSnapshot = useMemo(() => {
    // Optimisation score: impressions-weighted mean across campaigns with a
    // score. Simple mean if total impressions are zero (fresh accounts).
    const scoreRows = (campaignStatusData ?? []).filter(c => c.optimization_score != null);
    let optScore: number | null = null;
    if (scoreRows.length > 0) {
      const totalImp = scoreRows.reduce((s, c) => s + (c.impressions || 0), 0);
      if (totalImp > 0) {
        optScore = scoreRows.reduce((s, c) => s + (c.optimization_score ?? 0) * (c.impressions || 0), 0) / totalImp;
      } else {
        optScore = scoreRows.reduce((s, c) => s + (c.optimization_score ?? 0), 0) / scoreRows.length;
      }
    }

    // Search IS coverage: same impressions-weighted mean over the IS values
    // we have. Skips campaigns without IS data (e.g. Display).
    const isRows = (campaignStatusData ?? []).filter(c => c.search_impression_share != null);
    let isCoverage: number | null = null;
    if (isRows.length > 0) {
      const totalImp = isRows.reduce((s, c) => s + (c.impressions || 0), 0);
      if (totalImp > 0) {
        isCoverage = isRows.reduce((s, c) => s + (c.search_impression_share ?? 0) * (c.impressions || 0), 0) / totalImp;
      } else {
        isCoverage = isRows.reduce((s, c) => s + (c.search_impression_share ?? 0), 0) / isRows.length;
      }
    }

    // Budget utilisation %: weighted by daily budget so the account-level
    // number means "share of the account's total daily budget being spent" —
    // a simple mean let a €1/day campaign count like a €100/day one.
    let budgetUtil: number | null = null;
    if (budgetUtilisation.length > 0) {
      const totalBudget = budgetUtilisation.reduce((s, b) => s + b.dailyBudget, 0);
      budgetUtil = totalBudget > 0
        ? budgetUtilisation.reduce((s, b) => s + b.utilisation * b.dailyBudget, 0) / totalBudget
        : budgetUtilisation.reduce((s, b) => s + b.utilisation, 0) / budgetUtilisation.length;
    }

    // Change velocity: changes per week over the selected period (capped at
    // 30 days because Google Ads' change_event retention does — we say so
    // in the tile subtitle).
    let changeVelocity: number | null = null;
    if (activityData && activityData.length > 0) {
      const effectiveDays = Math.min(dateRangeFilter.days, 30);
      const weeks = effectiveDays / 7;
      if (weeks > 0) changeVelocity = activityData.length / weeks;
    }

    return { optScore, isCoverage, budgetUtil, changeVelocity };
  }, [campaignStatusData, budgetUtilisation, activityData, dateRangeFilter.days]);

  // Spend efficiency trends: the second half of the selected window against
  // its first half. Not the previous period, which is only fetched when the
  // comparison switcher is on, so the wording says halves (#7202).
  const spendTrends = useMemo(() => {
    if (chartData.length < 4) return null;
    const mid = Math.floor(chartData.length / 2);
    const firstHalf = chartData.slice(0, mid);
    const secondHalf = chartData.slice(mid);

    const sum = (arr: typeof chartData, key: 'cost' | 'conversions' | 'conversionValue') =>
      arr.reduce((s, d) => s + (d[key] || 0), 0);

    const prevCost = sum(firstHalf, 'cost');
    const prevConv = sum(firstHalf, 'conversions');
    const prevConvValue = sum(firstHalf, 'conversionValue');
    const currCost = sum(secondHalf, 'cost');
    const currConv = sum(secondHalf, 'conversions');
    const currConvValue = sum(secondHalf, 'conversionValue');

    const prevCpa = prevConv > 0 ? prevCost / prevConv : 0;
    const currCpa = currConv > 0 ? currCost / currConv : 0;
    const prevRoas = prevCost > 0 ? prevConvValue / prevCost : 0;
    const currRoas = currCost > 0 ? currConvValue / currCost : 0;

    const cpaDelta = prevCpa > 0 ? ((currCpa - prevCpa) / prevCpa) * 100 : 0;
    const roasDelta = prevRoas > 0 ? ((currRoas - prevRoas) / prevRoas) * 100 : 0;

    return {
      cpa: { current: currCpa, delta: cpaDelta, hasData: currConv > 0 && prevConv > 0 },
      roas: { current: currRoas, delta: roasDelta, hasData: currRoas > 0 && prevRoas > 0 },
    };
  }, [chartData]);

  // Problem alerts — only surfaces issues that exist
  const problemAlerts = useMemo(() => {
    const alerts: { severity: 'warning' | 'critical'; message: string; campaign?: string }[] = [];

    for (const c of campaignMetrics) {
      // High spend, zero conversions
      if (c.cost > 50 && c.conversions === 0) {
        alerts.push({ severity: 'critical', message: `${cs}${c.cost.toFixed(0)} spent with no conversions`, campaign: c.name });
      }

      // Approaching or exceeding budget
      const bu = budgetUtilisation.find(b => b.name === c.name);
      if (bu && bu.utilisation >= 95) {
        alerts.push({ severity: 'critical', message: `Budget ${bu.utilisation >= 100 ? 'exceeded' : 'nearly exhausted'} (${bu.utilisation.toFixed(0)}%)`, campaign: c.name });
      } else if (bu && bu.utilisation >= 80) {
        alerts.push({ severity: 'warning', message: `Budget at ${bu.utilisation.toFixed(0)}% utilisation`, campaign: c.name });
      }
    }

    // CPA trend declining (only if we have conversion data)
    if (spendTrends?.cpa.hasData && spendTrends.cpa.delta > 30) {
      alerts.push({ severity: 'warning', message: `Cost per conversion increased ${spendTrends.cpa.delta.toFixed(0)}% in the second half of the period` });
    }

    // ROAS trend declining — only when ROAS is set up (conversion values tracked)
    if (spendTrends?.roas.hasData && spendTrends.roas.delta < -30) {
      alerts.push({ severity: 'warning', message: `Return on ad spend declined ${Math.abs(spendTrends.roas.delta).toFixed(0)}% in the second half of the period` });
    }

    return alerts;
  }, [campaignMetrics, budgetUtilisation, spendTrends, cs]);

  const hasData = campaignMetrics.length > 0;
  const costByType = typeMetrics.map(t => ({ name: t.label, value: t.cost }));
  const convByType = typeMetrics.map(t => ({ name: t.label, value: t.conversions }));

  return {
    allCampaignsError,
    activeCampaigns,
    campaignIds,
    dateRangeFilter,
    dateRangeLabel,
    adData,
    campaignStatusData,
    campaignStatusByGoogleId,
    searchTermsData,
    isSearchTermsLoading,
    activityData,
    isActivityLoading,
    insightsData,
    isInsightsLoading,
    conversionActionsData,
    isConversionActionsLoading,
    notableSearchTermsByCampaign,
    conversionMixByAction,
    timeSeriesData,
    timeSeriesError,
    previousTimeSeriesError,
    isLoading,
    campaignMetrics,
    previousCampaignTotalsById,
    previousAdMetricsByAdId,
    adGroupMetrics,
    filteredCampaigns,
    typeMetrics,
    totals,
    previousTotals,
    adMetrics,
    chartData,
    deltaCards,
    actionLogs,
    actionDatesOnChart,
    userEditDatesOnChart,
    hasWeekendDays,
    budgetUtilisation,
    accountSnapshot,
    spendTrends,
    problemAlerts,
    hasData,
    costByType,
    convByType,
  };
}
